'use client';

import { useTranslations } from 'next-intl';
import { Shield, Eye, Lock, Settings, CreditCard, Calendar, BarChart3, Mail } from 'lucide-react';
import LegalPageLayout from '@/components/legal/LegalPageLayout';

export default function Datenschutz() {
  const t = useTranslations();

  return (
    <LegalPageLayout
      titleKey="legal.privacy.title"
      subtitleKey="legal.privacy.subtitle"
      icon={Shield}
      iconColor="text-blue-600"
    >
      {/* Overview Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <Eye className="mr-3 h-8 w-8 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.privacy.sections.overview.title')}
          </h2>
        </div>

        <h3 className="mb-3 text-xl font-semibold text-gray-700">
          {t('legal.privacy.sections.overview.generalNotice.title')}
        </h3>
        <p className="text-blue-700">{t('legal.privacy.sections.overview.generalNotice.text')}</p>
      </div>

      {/* Hosting Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <h2 className="mb-4 text-2xl font-bold text-gray-950">
          {t('legal.privacy.sections.hosting.title')}
        </h2>
        <p className="mb-4 text-purple-700">{t('legal.privacy.sections.hosting.intro')}</p>

        <div className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-6">
          <h3 className="mb-3 text-xl font-semibold text-gray-700">
            {t('legal.privacy.sections.hosting.vercel.title')}
          </h3>
          <p className="mb-2 text-purple-700">
            <strong>{t('legal.privacy.sections.hosting.vercel.provider')}</strong>{' '}
            {t('legal.privacy.sections.hosting.vercel.providerInfo')}
          </p>
          <p className="mb-2 text-purple-700">
            {t('legal.privacy.sections.hosting.vercel.description')}
          </p>
          <p className="mb-2 text-purple-700">
            <strong>{t('legal.privacy.sections.hosting.vercel.legalBasis')}</strong>{' '}
            {t('legal.privacy.sections.hosting.vercel.legalBasisInfo')}
          </p>
          <p className="text-purple-700">
            <strong>{t('legal.privacy.sections.hosting.vercel.privacyPolicy')}</strong>{' '}
            <a
              href={t('legal.privacy.sections.hosting.vercel.privacyPolicyLink')}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('legal.privacy.sections.hosting.vercel.privacyPolicyLink')}
            </a>
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-6">
          <h3 className="mb-3 text-xl font-semibold text-gray-700">
            {t('legal.privacy.sections.hosting.supabase.title')}
          </h3>
          <p className="mb-2 text-purple-700">
            <strong>{t('legal.privacy.sections.hosting.supabase.provider')}</strong>{' '}
            {t('legal.privacy.sections.hosting.supabase.providerInfo')}
          </p>
          <p className="mb-2 text-purple-700">
            {t('legal.privacy.sections.hosting.supabase.description')}
          </p>
          <p className="mb-2 text-purple-700">
            <strong>{t('legal.privacy.sections.hosting.supabase.legalBasis')}</strong>{' '}
            {t('legal.privacy.sections.hosting.supabase.legalBasisInfo')}
          </p>
          <p className="text-purple-700">
            <strong>{t('legal.privacy.sections.hosting.supabase.privacyPolicy')}</strong>{' '}
            <a
              href={t('legal.privacy.sections.hosting.supabase.privacyPolicyLink')}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('legal.privacy.sections.hosting.supabase.privacyPolicyLink')}
            </a>
          </p>
        </div>
      </div>

      {/* Payment Section - Stripe */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <CreditCard className="mr-3 h-8 w-8 text-emerald-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.privacy.sections.payment.title')}
          </h2>
        </div>
        <p className="mb-4 text-emerald-700">{t('legal.privacy.sections.payment.intro')}</p>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-6">
          <h3 className="mb-3 text-xl font-semibold text-gray-700">
            {t('legal.privacy.sections.payment.stripe.title')}
          </h3>
          <p className="mb-2 text-emerald-700">
            <strong>{t('legal.privacy.sections.payment.stripe.provider')}</strong>{' '}
            {t('legal.privacy.sections.payment.stripe.providerInfo')}
          </p>
          <p className="mb-2 text-emerald-700">
            {t('legal.privacy.sections.payment.stripe.description')}
          </p>
          <p className="mb-2 text-emerald-700">
            <strong>{t('legal.privacy.sections.payment.stripe.dataProcessed')}</strong>{' '}
            {t('legal.privacy.sections.payment.stripe.dataProcessedInfo')}
          </p>
          <p className="mb-2 text-emerald-700">
            <strong>{t('legal.privacy.sections.payment.stripe.purpose')}</strong>{' '}
            {t('legal.privacy.sections.payment.stripe.purposeInfo')}
          </p>
          <p className="mb-2 text-emerald-700">
            <strong>{t('legal.privacy.sections.payment.stripe.legalBasis')}</strong>{' '}
            {t('legal.privacy.sections.payment.stripe.legalBasisInfo')}
          </p>
          <p className="mb-2 text-emerald-700">
            <strong>{t('legal.privacy.sections.payment.stripe.retention')}</strong>{' '}
            {t('legal.privacy.sections.payment.stripe.retentionInfo')}
          </p>
          <p className="mb-2 text-emerald-700">
            <strong>{t('legal.privacy.sections.payment.stripe.thirdCountry')}</strong>{' '}
            {t('legal.privacy.sections.payment.stripe.thirdCountryInfo')}
          </p>
          <p className="text-emerald-700">
            <strong>{t('legal.privacy.sections.payment.stripe.privacyPolicy')}</strong>{' '}
            <a
              href={t('legal.privacy.sections.payment.stripe.privacyPolicyLink')}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('legal.privacy.sections.payment.stripe.privacyPolicyLink')}
            </a>
          </p>
        </div>
      </div>

      {/* Calendar Integration Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <Calendar className="mr-3 h-8 w-8 text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.privacy.sections.calendar.title')}
          </h2>
        </div>
        <p className="mb-4 text-indigo-700">{t('legal.privacy.sections.calendar.intro')}</p>

        {/* Google Calendar */}
        <div className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-6">
          <h3 className="mb-3 text-xl font-semibold text-gray-700">
            {t('legal.privacy.sections.calendar.google.title')}
          </h3>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.provider')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.providerInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            {t('legal.privacy.sections.calendar.google.description')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.dataProcessed')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.dataProcessedInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.purpose')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.purposeInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.legalBasis')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.legalBasisInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.security')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.securityInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.dataMinimization')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.dataMinimizationInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.retention')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.retentionInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.deletion')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.deletionInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.thirdCountry')}</strong>{' '}
            {t('legal.privacy.sections.calendar.google.thirdCountryInfo')}
          </p>
          <p className="text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.google.privacyPolicy')}</strong>{' '}
            <a
              href={t('legal.privacy.sections.calendar.google.privacyPolicyLink')}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('legal.privacy.sections.calendar.google.privacyPolicyLink')}
            </a>
          </p>
        </div>

        {/* Apple iCloud Calendar */}
        <div className="rounded-2xl border border-gray-200/80 bg-white p-6">
          <h3 className="mb-3 text-xl font-semibold text-gray-700">
            {t('legal.privacy.sections.calendar.apple.title')}
          </h3>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.apple.provider')}</strong>{' '}
            {t('legal.privacy.sections.calendar.apple.providerInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            {t('legal.privacy.sections.calendar.apple.description')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.apple.dataProcessed')}</strong>{' '}
            {t('legal.privacy.sections.calendar.apple.dataProcessedInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.apple.purpose')}</strong>{' '}
            {t('legal.privacy.sections.calendar.apple.purposeInfo')}
          </p>
          <p className="mb-2 text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.apple.legalBasis')}</strong>{' '}
            {t('legal.privacy.sections.calendar.apple.legalBasisInfo')}
          </p>
          <p className="text-indigo-700">
            <strong>{t('legal.privacy.sections.calendar.apple.privacyPolicy')}</strong>{' '}
            <a
              href={t('legal.privacy.sections.calendar.apple.privacyPolicyLink')}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('legal.privacy.sections.calendar.apple.privacyPolicyLink')}
            </a>
          </p>
        </div>
      </div>

      {/* Responsible Party Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <Settings className="mr-3 h-8 w-8 text-green-600" />
          <h3 className="text-xl font-semibold text-gray-950">
            {t('legal.privacy.sections.responsible.title')}
          </h3>
        </div>
        <p className="mb-4 text-green-700">{t('legal.privacy.sections.responsible.intro')}</p>
        <div className="rounded-2xl border border-gray-200/80 bg-white p-4">
          <p className="font-medium text-gray-700">
            <strong>{t('legal.company.name')}</strong>
            <br />
            {t('legal.company.address')}
            <br />
            {t('legal.company.city')}
            <br />
            {t('legal.company.country')}
          </p>
          <p className="mt-3 text-green-700">
            Telefon:{' '}
            <a href={`tel:${t('legal.company.phone')}`} className="text-blue-600 hover:underline">
              {t('legal.company.phone')}
            </a>
            <br />
            E-Mail:{' '}
            <a
              href={`mailto:${t('legal.company.email')}`}
              className="text-blue-600 hover:underline"
            >
              {t('legal.company.email')}
            </a>
          </p>
        </div>
      </div>

      {/* Booking System Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <h2 className="mb-4 text-2xl font-bold text-gray-950">
          {t('legal.privacy.sections.booking.title')}
        </h2>

        <div className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-6">
          <h3 className="mb-3 text-xl font-semibold text-gray-700">
            {t('legal.privacy.sections.booking.processing.title')}
          </h3>
          <p className="mb-3 text-yellow-700">
            {t('legal.privacy.sections.booking.processing.intro')}
          </p>
          <ul className="ml-4 space-y-1 text-yellow-700">
            {(t.raw('legal.privacy.sections.booking.processing.purposes') as string[]).map(
              (purpose, index) => (
                <li key={index}>• {purpose}</li>
              )
            )}
          </ul>
          <p className="mt-3 text-yellow-700">
            <strong>{t('legal.privacy.sections.booking.processing.legalBasis')}</strong>{' '}
            {t('legal.privacy.sections.booking.processing.legalBasisInfo')}
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-6">
          <h3 className="mb-3 text-xl font-semibold text-gray-700">
            {t('legal.privacy.sections.booking.transmission.title')}
          </h3>
          <p className="mb-3 text-yellow-700">
            {t('legal.privacy.sections.booking.transmission.intro')}
          </p>
          <ul className="ml-4 space-y-1 text-yellow-700">
            {(t.raw('legal.privacy.sections.booking.transmission.recipients') as string[]).map(
              (recipient, index) => (
                <li key={index}>• {recipient}</li>
              )
            )}
          </ul>
          <p className="mt-3 text-yellow-700">
            <strong>{t('legal.privacy.sections.booking.transmission.retention')}</strong>{' '}
            {t('legal.privacy.sections.booking.transmission.retentionInfo')}
          </p>
        </div>
      </div>

      {/* Analytics & Marketing Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <BarChart3 className="mr-3 h-8 w-8 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.privacy.sections.analytics.sectionTitle')}
          </h2>
        </div>
        <p className="mb-6 text-gray-700">
          {t('legal.privacy.sections.analytics.sectionIntro')}
        </p>

        {(['posthog', 'clarity', 'sentry', 'googleAds', 'resend'] as const).map((key) => (
          <div key={key} className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-6">
            <h3 className="mb-3 text-xl font-semibold text-gray-700">
              {t(`legal.privacy.sections.analytics.${key}.title`)}
            </h3>
            <p className="mb-2 text-gray-700">
              <strong>{t('legal.privacy.sections.analytics.provider')}</strong>{' '}
              {t(`legal.privacy.sections.analytics.${key}.providerInfo`)}
            </p>
            <p className="mb-2 text-gray-700">
              {t(`legal.privacy.sections.analytics.${key}.description`)}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>{t('legal.privacy.sections.analytics.dataProcessed')}</strong>{' '}
              {t(`legal.privacy.sections.analytics.${key}.dataProcessedInfo`)}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>{t('legal.privacy.sections.analytics.purpose')}</strong>{' '}
              {t(`legal.privacy.sections.analytics.${key}.purposeInfo`)}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>{t('legal.privacy.sections.analytics.legalBasis')}</strong>{' '}
              {t(`legal.privacy.sections.analytics.${key}.legalBasisInfo`)}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>{t('legal.privacy.sections.analytics.retention')}</strong>{' '}
              {t(`legal.privacy.sections.analytics.${key}.retentionInfo`)}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>{t('legal.privacy.sections.analytics.thirdCountry')}</strong>{' '}
              {t(`legal.privacy.sections.analytics.${key}.thirdCountryInfo`)}
            </p>
            <p className="text-gray-700">
              <strong>{t('legal.privacy.sections.analytics.privacyPolicy')}</strong>{' '}
              <a
                href={t(`legal.privacy.sections.analytics.${key}.privacyPolicyLink`)}
                className="text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(`legal.privacy.sections.analytics.${key}.privacyPolicyLink`)}
              </a>
            </p>
          </div>
        ))}

        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6">
          <h3 className="mb-2 text-lg font-semibold text-blue-950">
            {t('legal.privacy.sections.analytics.consentNote.title')}
          </h3>
          <p className="text-blue-900">
            {t('legal.privacy.sections.analytics.consentNote.body')}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 rounded-2xl bg-gray-100 p-6 text-center">
        <Lock className="mx-auto mb-3 h-8 w-8 text-gray-600" />
        <p className="text-sm text-gray-600">
          {t('legal.privacy.lastUpdated', { date: new Date().toLocaleDateString() })}
        </p>
        <p className="mt-2 text-xs text-gray-500">
          {t('legal.privacy.contact')}{' '}
          <a
            href={`mailto:${t('legal.company.email')}`}
            className="text-blue-600 hover:underline"
          >
            {t('legal.company.email')}
          </a>
        </p>
      </div>
    </LegalPageLayout>
  );
}
